import React from "react";
import LegalPage from "@/pages/LegalPage";

// Light helpers for readable markup inside the long legal prose.
const P = ({ children }) => <p>{children}</p>;
const L = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 marker:text-secondary">{children}</ul>;
const Li = ({ children }) => <li>{children}</li>;
const B = ({ children }) => <span className="font-semibold text-ocean-700">{children}</span>;
const Note = ({ children }) => (
    <div className="rounded-xl bg-sand-100 border border-sand-200 p-4 text-sm text-ocean-700">
        {children}
    </div>
);

const SECTIONS = [
    {
        id: "acceptance",
        title: "Acceptance of these Terms",
        content: (
            <>
                <P>
                    These Terms of Service (the "<B>Terms</B>") form a legally binding agreement between <B>Modern Business Solutions Ltd</B>, a company incorporated in the Cayman Islands ("<B>Company</B>", "<B>we</B>", "<B>us</B>", or "<B>our</B>"), and the natural or legal person accessing or using the BlueLedger platform, websites, and related services (the "<B>Service</B>") (the "<B>Customer</B>", "<B>you</B>", or "<B>your</B>").
                </P>
                <P>
                    By creating an account, clicking "I agree", accessing the Service, or otherwise indicating acceptance, you represent that (a) you have read and understood these Terms, (b) you have the legal authority to bind yourself and any organization on whose behalf you act, and (c) you agree to be bound by these Terms together with our <a href="/privacy" className="text-secondary-dark hover:underline font-medium">Privacy Policy</a>, which is incorporated by reference. If you do not agree, do not use the Service.
                </P>
                <P>
                    These Terms are executed electronically pursuant to the Cayman Islands Electronic Transactions Act (as revised) and have the same legal effect as a signed written agreement.
                </P>
            </>
        ),
    },
    {
        id: "the-service",
        title: "The Service",
        content: (
            <>
                <P>
                    BlueLedger is a cloud-based accounting and financial management platform offered on a software-as-a-service basis. Features include, without limitation, invoicing, bill tracking, expense capture and OCR, bank transaction import and reconciliation, chart of accounts, journal entries, reporting, multi-user collaboration with role-based access, and data import/export utilities.
                </P>
                <P>
                    We reserve the right to modify, enhance, suspend, or discontinue any part of the Service at our sole discretion, temporarily or permanently, with or without notice. We will use commercially reasonable efforts to give notice of material adverse changes to paying Customers.
                </P>
            </>
        ),
    },
    {
        id: "no-professional-advice",
        title: "No Professional Advice — Accounting, Tax, or Legal",
        content: (
            <>
                <Note>
                    <B>Important.</B> BlueLedger is a software tool, not a certified public accountant, tax advisor, auditor, or law firm. Nothing provided by the Service constitutes accounting, tax, legal, financial, or regulatory advice.
                </Note>
                <P>
                    Any suggestions, defaults, categorizations, or reports produced by the Service — including those generated with the assistance of artificial intelligence (such as receipt OCR and transaction categorization) — are provided for informational purposes only. You are solely responsible for the accuracy, completeness, and regulatory compliance of your books, records, filings, and decisions.
                </P>
                <P>
                    You acknowledge that accounting treatment, tax positions, corporate filings, and reporting obligations vary by jurisdiction and by facts specific to your business, and you agree to engage qualified professional advisors of your choosing. Cayman Islands "no-tax mode" is a configuration default and is not a tax opinion; you remain responsible for determining the correct tax treatment for your entity.
                </P>
            </>
        ),
    },
    {
        id: "accounts",
        title: "Accounts, Eligibility, and Security",
        content: (
            <>
                <P>
                    To use the Service, you must register for an account and provide accurate, current information. You must be at least 18 years old and capable of entering into a binding contract. You may not use the Service if you are prohibited by applicable law from doing so.
                </P>
                <L>
                    <Li>You are responsible for all activity under your account, including actions taken by team members you invite.</Li>
                    <Li>You must keep your credentials confidential and notify us immediately at <a className="text-secondary-dark hover:underline" href="mailto:security@blueledger.io">security@blueledger.io</a> of any unauthorized access or suspected compromise.</Li>
                    <Li>We are not liable for any loss or damage arising from your failure to safeguard your credentials.</Li>
                    <Li>We may refuse registration, suspend, or terminate accounts at our discretion, including for violations of these Terms.</Li>
                </L>
            </>
        ),
    },
    {
        id: "subscription",
        title: "Subscriptions, Fees, and Billing",
        content: (
            <>
                <P>
                    The Service is offered under the plans described on our <a href="/pricing" className="text-secondary-dark hover:underline font-medium">Pricing page</a> (Free, Pro, and Team, or successor plans). Paid plans are billed in advance on a monthly or annual cycle. All fees are quoted in United States Dollars (USD) unless stated otherwise and are exclusive of any applicable taxes, duties, or fees (of which, in Cayman, there are ordinarily none for standard services).
                </P>
                <L>
                    <Li><B>Auto-renewal.</B> Paid subscriptions automatically renew at the end of each billing cycle at the then-current price unless you cancel at least 24 hours before renewal.</Li>
                    <Li><B>Failed payments.</B> If we cannot collect fees, we may suspend paid features and downgrade you to the Free plan. Your data is preserved and your account remains accessible.</Li>
                    <Li><B>No refunds.</B> Except where required by applicable law, all fees are non-refundable, including for partially used periods, unused features, or unused seats.</Li>
                    <Li><B>Price changes.</B> We may change prices on at least 30 days' notice, effective at your next renewal.</Li>
                    <Li><B>Taxes.</B> You are responsible for any taxes, levies, or duties imposed by any jurisdiction in connection with your use of the Service, excluding taxes on our net income.</Li>
                </L>
            </>
        ),
    },
    {
        id: "customer-data",
        title: "Customer Data and Ownership",
        content: (
            <>
                <P>
                    As between the parties, you retain all right, title, and interest in and to the data, records, documents, images, and other content you submit to the Service ("<B>Customer Data</B>"). You grant us a worldwide, royalty-free, non-exclusive license to host, store, reproduce, modify (for technical purposes such as reformatting for display), transmit, and display Customer Data solely to operate, maintain, support, and improve the Service and to comply with law.
                </P>
                <P>
                    You represent and warrant that (a) you own or have all necessary rights to the Customer Data and to grant this license, (b) Customer Data does not infringe, misappropriate, or violate any third party's rights, (c) you have obtained all consents and given all notices required to lawfully process any personal data you upload, and (d) Customer Data complies with applicable law.
                </P>
                <P>
                    We may process de-identified and aggregated usage data to analyze, improve, and develop the Service without restriction.
                </P>
            </>
        ),
    },
    {
        id: "ip",
        title: "Our Intellectual Property",
        content: (
            <>
                <P>
                    The Service, including all software, databases, designs, logos, trademarks, trade names (including "BlueLedger"), documentation, APIs, and other content (excluding Customer Data), is and remains the exclusive property of the Company and its licensors, protected by copyright, trademark, trade secret, and other laws. We grant you a limited, non-exclusive, non-transferable, revocable right to access and use the Service during your subscription solely for your internal business purposes and subject to these Terms.
                </P>
                <P>
                    You will not, and will not permit any third party to: (i) copy, modify, or create derivative works of the Service; (ii) reverse engineer, decompile, or disassemble any part of the Service, except to the extent this restriction is prohibited by law; (iii) resell, sublicense, rent, or lease the Service; (iv) circumvent or disable any security or access control; (v) use the Service to build a competing product; or (vi) remove or obscure any proprietary notices.
                </P>
                <P>
                    <B>Feedback.</B> If you provide suggestions, ideas, or feedback, you grant us a perpetual, irrevocable, royalty-free, worldwide license to use, exploit, and incorporate such feedback without obligation.
                </P>
            </>
        ),
    },
    {
        id: "acceptable-use",
        title: "Acceptable Use",
        content: (
            <>
                <P>You agree not to, and not to permit others to:</P>
                <L>
                    <Li>Use the Service in violation of any applicable law, regulation, or sanctions regime (including the laws of the Cayman Islands);</Li>
                    <Li>Upload, store, or transmit any content that is unlawful, defamatory, obscene, fraudulent, infringing, or otherwise objectionable;</Li>
                    <Li>Upload viruses, malware, or other harmful code;</Li>
                    <Li>Attempt to gain unauthorized access to the Service or to any systems or networks connected to the Service;</Li>
                    <Li>Interfere with, disrupt, or impose an unreasonable load on the Service, including via automated scraping, crawling, or bulk querying except through our documented APIs and within published rate limits;</Li>
                    <Li>Use the Service to process or store personal data in a manner that violates applicable data protection laws;</Li>
                    <Li>Use the Service for any activity that we reasonably determine may expose us, our customers, or our infrastructure to liability or harm.</Li>
                </L>
                <P>
                    We may investigate and take appropriate action, including suspension or termination, for any violation of this section.
                </P>
            </>
        ),
    },
    {
        id: "third-parties",
        title: "Third-Party Services and AI",
        content: (
            <>
                <P>
                    The Service may integrate with, link to, or rely on third-party products and services (for example, payment processors such as PayPal, email providers, cloud infrastructure providers, authentication providers such as Google, and AI providers such as OpenAI for receipt OCR). Your use of such third-party services is governed by their own terms and privacy policies. We are not responsible for the availability, accuracy, or content of any third-party service.
                </P>
                <P>
                    AI-generated outputs may be incomplete or inaccurate. You are responsible for reviewing and verifying such outputs before relying on them for any business, accounting, tax, legal, or operational decision.
                </P>
            </>
        ),
    },
    {
        id: "availability",
        title: "Availability and Support",
        content: (
            <>
                <P>
                    We will use commercially reasonable efforts to make the Service available 24/7, subject to scheduled maintenance, emergency maintenance, third-party outages, and force majeure events. <B>We do not provide a contractual uptime guarantee or service-level agreement</B> unless expressly agreed in a separate written order form.
                </P>
                <P>
                    Standard support is provided via email at <a className="text-secondary-dark hover:underline" href="mailto:support@blueledger.io">support@blueledger.io</a> during business hours in the Cayman Islands (UTC-5). Enhanced support is available on higher plans as described on the Pricing page.
                </P>
            </>
        ),
    },
    {
        id: "disclaimer",
        title: "Disclaimer of Warranties",
        content: (
            <>
                <P className="uppercase text-[13px] tracking-wide">
                    <B>The Service is provided on an "as is" and "as available" basis, without warranty of any kind, whether express, implied, statutory, or otherwise.</B> To the maximum extent permitted by applicable law, we expressly disclaim all warranties, including without limitation implied warranties of merchantability, fitness for a particular purpose, non-infringement, title, quiet enjoyment, accuracy of data, and any warranty arising from course of dealing or usage of trade.
                </P>
                <P>
                    Without limiting the foregoing, we do not warrant that the Service will be uninterrupted, error-free, secure, or free from viruses or harmful components; that defects will be corrected; that any data generated, transmitted, or stored through the Service will be accurate, complete, or reliable; or that the Service will meet your requirements. No advice or information, whether oral or written, obtained from the Company creates any warranty not expressly stated in these Terms.
                </P>
            </>
        ),
    },
    {
        id: "liability",
        title: "Limitation of Liability",
        content: (
            <>
                <P className="uppercase text-[13px] tracking-wide">
                    <B>To the maximum extent permitted by applicable law, in no event will the Company, its affiliates, directors, officers, employees, agents, or licensors be liable to you or any third party for any indirect, incidental, special, consequential, exemplary, or punitive damages, including without limitation loss of profits, revenue, data, goodwill, business opportunity, regulatory fines, or the cost of substitute services, arising out of or in connection with the Service or these Terms, whether in contract, tort (including negligence), strict liability, or any other theory, and whether or not we have been advised of the possibility of such damages.</B>
                </P>
                <P className="uppercase text-[13px] tracking-wide">
                    <B>In any event, our aggregate liability arising out of or in connection with the Service or these Terms will not exceed the greater of (i) the total fees actually paid by you to us in the twelve (12) months immediately preceding the event giving rise to the claim, or (ii) one hundred United States Dollars (US$100).</B>
                </P>
                <P>
                    Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities; in such jurisdictions our liability is limited to the smallest extent permitted by law.
                </P>
            </>
        ),
    },
    {
        id: "indemnification",
        title: "Indemnification by You",
        content: (
            <>
                <P>
                    You agree to defend, indemnify, and hold harmless the Company and its affiliates, directors, officers, employees, contractors, and agents from and against any and all claims, damages, liabilities, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to: (i) your or your users' access to or use of the Service; (ii) your Customer Data or the processing of it through the Service; (iii) your breach or alleged breach of these Terms or of any representations, warranties, or covenants contained herein; (iv) your violation of any applicable law or the rights of any third party, including intellectual property, privacy, or publicity rights.
                </P>
                <P>
                    We reserve the right, at our own expense, to assume the exclusive defense and control of any matter subject to indemnification by you, and you agree to cooperate with our defense of such claim.
                </P>
            </>
        ),
    },
    {
        id: "termination",
        title: "Suspension and Termination",
        content: (
            <>
                <P>
                    You may cancel your subscription at any time from within the Service. We may suspend or terminate your access to the Service, in whole or in part, with or without cause and with or without notice, including if (a) you breach these Terms, (b) your use creates a security or legal risk, (c) your payment fails, (d) we are required to do so by law or governmental request, or (e) we discontinue the Service.
                </P>
                <P>
                    Upon termination, your right to access the Service ends. Paid subscriptions that are cancelled by you will remain active until the end of the current billing period. Upon termination for cause by us, we may delete your Customer Data after a reasonable period. You may request an export of your Customer Data prior to termination using the in-app Import/Export tools.
                </P>
                <P>
                    Sections intended by their nature to survive termination (including without limitation sections on Intellectual Property, Customer Data licenses, Disclaimer, Limitation of Liability, Indemnification, Governing Law, and General) will survive.
                </P>
            </>
        ),
    },
    {
        id: "changes",
        title: "Changes to the Service and Terms",
        content: (
            <>
                <P>
                    We may update these Terms from time to time. If we make material changes, we will notify you via email or through the Service at least 15 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not agree, you must stop using the Service and may terminate your subscription as described above.
                </P>
            </>
        ),
    },
    {
        id: "governing-law",
        title: "Governing Law and Dispute Resolution",
        content: (
            <>
                <P>
                    These Terms are governed by and construed in accordance with the laws of the <B>Cayman Islands</B>, without regard to its conflict of laws principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply.
                </P>
                <P>
                    You agree that any dispute, claim, or controversy arising out of or in connection with these Terms or the Service shall be subject to the <B>exclusive jurisdiction of the courts of the Cayman Islands</B>, and you irrevocably submit to the jurisdiction of such courts. Nothing in this clause prevents us from seeking injunctive or other equitable relief in any competent court to protect our intellectual property, confidential information, or system security.
                </P>
                <P>
                    <B>No class actions.</B> You agree that any claim will be brought in an individual capacity and not as part of any class, collective, or representative proceeding.
                </P>
                <P>
                    <B>Time limit.</B> Any cause of action must be commenced within one (1) year after it arises, except where prohibited by law.
                </P>
            </>
        ),
    },
    {
        id: "general",
        title: "General",
        content: (
            <>
                <L>
                    <Li><B>Entire agreement.</B> These Terms, together with the Privacy Policy and any order form or additional terms expressly referenced, constitute the entire agreement between the parties and supersede all prior or contemporaneous understandings.</Li>
                    <Li><B>Severability.</B> If any provision is held unenforceable, the remaining provisions will remain in full force and effect.</Li>
                    <Li><B>No waiver.</B> Failure to enforce any provision is not a waiver of it.</Li>
                    <Li><B>Assignment.</B> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms freely, including in connection with a merger, acquisition, or sale of assets.</Li>
                    <Li><B>Force majeure.</B> We are not liable for any delay or failure caused by events beyond our reasonable control, including acts of God, war, terrorism, labor disputes, hurricane, internet outages, or governmental action.</Li>
                    <Li><B>Relationship.</B> The parties are independent contractors. No partnership, joint venture, agency, or employment relationship is created.</Li>
                    <Li><B>Notices.</B> We may give notice by email, in-app message, or posting on our website. You may give us notice at <a className="text-secondary-dark hover:underline" href="mailto:legal@blueledger.io">legal@blueledger.io</a>.</Li>
                    <Li><B>Export controls.</B> You will comply with all applicable export, sanctions, and anti-corruption laws.</Li>
                    <Li><B>Third-party beneficiaries.</B> There are no third-party beneficiaries of these Terms.</Li>
                </L>
            </>
        ),
    },
];

export default function TermsOfService() {
    return (
        <LegalPage
            title="Terms of Service"
            effectiveDate="1 May 2026"
            version="1.0"
            sections={SECTIONS}
        />
    );
}
